import { Button, Form, Error, AutocompleteFieldRhf, InputFieldRhf, Separator } from '@my/ui'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import * as z from 'zod'
import { GroupCreationHeader } from './group-creation-header'

const formSchema = z.object({
  mainCategory: z
    .object(
      {
        label: z.string(),
        value: z.string(),
      },
      { invalid_type_error: 'Main category is required' }
    )
    .refine((data) => data.label && data.value, {
      message: 'Main category is required',
    }),
  subCategories: z
    .array(
      z.object({
        label: z.string(),
        value: z.string(),
      })
    )
    .max(6, "You can't add more than 6 subcategories")
    .default([]),
  description: z
    .string()
    .max(500, 'Description must be at most 500 characters')
    .optional()
})

export type GroupCreationFormCategoriesValues = z.infer<typeof formSchema>

export interface GroupCreationFormCategoriesProps {
  onBack: () => void
  error?: string
  defaultValues?: Partial<GroupCreationFormCategoriesValues>
  groupCategoriesOptions: {
    label: string
    value: string
  }[]
  onSubmit: (values: GroupCreationFormCategoriesValues) => void
}

export const GroupCreationFormCategories = (props: GroupCreationFormCategoriesProps) => {
  const { control, handleSubmit } = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: {
      mainCategory: {
        label: '',
        value: '',
      },
      subCategories: [],
      description: '',
      ...props.defaultValues,
    },
  })

  return (
    <Form
      gap="$3.5"
      w="100%"
      flex={1}
      onSubmit={handleSubmit((data) => {
        props.onSubmit(data)
      })}
    >
      <GroupCreationHeader title="Create Group" onBack={props.onBack}>
        <Form.Trigger asChild fd="row" ai="center">
          <Button size="$3" theme="primary">
            Next
          </Button>
        </Form.Trigger>
      </GroupCreationHeader>

      <Separator boc="$overlay8" w="100%" />

      <AutocompleteFieldRhf
        name="mainCategory"
        control={control}
        label="Add Main Category"
        placeholderSearch="Search for main category..."
        placeholder="Select a main category..."
        required
        options={props.groupCategoriesOptions}
        showHelperText
        wrapperProps={{
          bw: 1,
          boc: "$gray4"
        }}
        popoverProps={{
          contentProps: {
            mr: '$4',
            minWidth: 300,
            maxWidth: 600,
          },
        }}
        labelProps={{
          fontSize: '$4',
          fow: '500',
        }}
      />

      <AutocompleteFieldRhf
        name="subCategories"
        control={control}
        label="Add Sub Category"
        placeholderSearch="Search for your sub category..."
        placeholder="Select a sub category..."
        multiple
        options={props.groupCategoriesOptions}
        showHelperText
        wrapperProps={{
          bw: 1,
          boc: "$gray4"
        }}
        popoverProps={{
          contentProps: {
            mr: '$4',
            minWidth: 300,
            maxWidth: 600,
          },
        }}
        labelProps={{
          fontSize: '$4',
          fow: '500',
        }}
      />

      <InputFieldRhf
        name="description"
        control={control}
        label="Add a description"
        placeholder="Describe your group"
        multiline
        fullWidth
        numberOfLines={4}
        minHeight={100}
        shadowOpacity={0}
        bw={1}
        boc="$gray4"
        showHelperText
        hoverStyle={{
          boc: '$gray6',
        }}
        focusStyle={{
          boc: '$gray7',
          outlineWidth: 0,
        }}
        labelProps={{
          fontSize: '$4',
          fow: '500',
        }}
      />

      {props.error && <Error error={props.error} />}
    </Form>
  )
}
